import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Activity,
} from "lucide-react";
import { useStocks } from "./useStocks";
import {StockRow} from "./StockRow";
import {StockCard} from "./StockCard";

export default function MvStocksPage() {
  const {
  data: stocks = [],
  isLoading,
} = useStocks();

  const gainers = useMemo(
    () =>
      [...stocks]
        .sort(
          (a: any, b: any) =>
            b.orderTrendChangePercent -
            a.orderTrendChangePercent
        )
        .slice(0, 5),
    [stocks]
  );

  const losers = useMemo(
    () =>
      [...stocks]
        .sort(
          (a: any, b: any) =>
            a.orderTrendChangePercent -
            b.orderTrendChangePercent
        )
        .slice(0, 5),
    [stocks]
  );

  if (isLoading) {
    return (
      <div className="p-6">
        Loading stocks...
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">

      {/* MARKET SUMMARY */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        <div className="rounded-3xl bg-white shadow p-5">
          <div className="flex items-center gap-2">
            <Activity size={18} />
            <span>Total Stocks</span>
          </div>

          <div className="text-3xl font-bold mt-3">
            {stocks.length}
          </div>
        </div>

        <div className="rounded-3xl bg-white shadow p-5">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} />
            <span>Gainers</span>
          </div>

          <div className="text-3xl font-bold mt-3">
            {
              stocks.filter(
                (x: any) =>
                  x.orderTrendChangeState === "up"
              ).length
            }
          </div>
        </div>

        <div className="rounded-3xl bg-white shadow p-5">
          <div className="flex items-center gap-2">
            <TrendingDown size={18} />
            <span>Losers</span>
          </div>

          <div className="text-3xl font-bold mt-3">
            {
              stocks.filter(
                (x: any) =>
                  x.orderTrendChangeState === "down"
              ).length
            }
          </div>
        </div>

      </div>

      {/* PRICE CHART */}

      <div className="rounded-3xl bg-white shadow p-6">

        <h2 className="font-semibold text-lg mb-4">
          Current Prices
        </h2>

        <div className="h-[400px]">

          <ResponsiveContainer width="100%" height="100%">

            <BarChart
              data={stocks
                .slice()
                .sort(
                  (a: any, b: any) =>
                    b.price - a.price
                )
                .slice(0, 10)}
            >
              <XAxis dataKey="symbol" />
              <Tooltip />

              <Bar dataKey="price" radius={[6, 6, 0, 0]} />

            </BarChart>

          </ResponsiveContainer>

        </div>

      </div>

      {/* GAINERS + LOSERS */}

      <div className="grid lg:grid-cols-2 gap-6">

        <div className="rounded-3xl bg-white shadow p-6">

          <h2 className="font-semibold mb-4">
            Top Gainers
          </h2>

          <div className="space-y-3">

            {gainers.map((stock: any) => (
              <StockRow
                key={stock.id}
                stock={stock}
              />
            ))}

          </div>

        </div>

        <div className="rounded-3xl bg-white shadow p-6">

          <h2 className="font-semibold mb-4">
            Top Losers
          </h2>

          <div className="space-y-3">

            {losers.map((stock: any) => (
              <StockRow
                key={stock.id}
                stock={stock}
              />
            ))}

          </div>

        </div>

      </div>

      {/* STOCK LIST */}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">

        {stocks.map((stock: any) => (
          <StockCard
            key={stock.id}
            stock={stock}
          />
        ))}

      </div>

    </div>
  );
}